import React from "react";

import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { BULK_HELP_LINKS } from "./bulkHelpLinks";
import { getBulkOwnerLines } from "./bulkOwner";
import { getBulkStatusBucket } from "./bulkStatus";
import "../../css/admin/utils/container-header.scss";
import "../../css/admin/utils/item-status.scss";
import "../../css/admin/utils/tooltip.scss";
import NavbarAvatarImage from "../common/header/NavbarAvatarImage";
import PageHeader from "../common/PageHeader";
import { BULKS_ROUTE } from "../routes/routes";

import type { JSX } from "react";

import type {
  BulkIngestOwner,
  BulkIngestStatus,
} from "../../api/BulkAPI.types";

interface BulkPageHeaderProps {
  isNew?: boolean;
  owner?: BulkIngestOwner;
  reference?: string;
  status?: BulkIngestStatus;
  title: string;
}

function BulkPageHeader({
  isNew = false,
  owner,
  reference,
  status,
  title,
}: BulkPageHeaderProps): JSX.Element {
  const { t } = useTranslation("editor-bulk");
  const history = useHistory();

  const breadcrumbs = [
    { name: t("sectionTitle"), url: BULKS_ROUTE },
    { name: t(isNew ? "new" : "edit") },
  ];

  const statusInfo =
    status === undefined
      ? null
      : {
          bucket: getBulkStatusBucket(status),
          label: t(`ingestStatus.${status}`, { defaultValue: status }),
        };

  const ownerTooltip = owner ? getBulkOwnerLines(owner) : [];

  function handleBack(): void {
    history.goBack();
  }

  return (
    <div className="flex flex-col">
      <PageHeader breadcrumbs={breadcrumbs} infoPanelLinks={BULK_HELP_LINKS} />

      <div className="editor-container-header">
        <div className="editor-header-body">
          <button
            aria-label={t("back")}
            className="editor-header-back-btn"
            onClick={handleBack}
            type="button"
          >
            <i className="material-icons editor-multi-icons">arrow_back</i>
          </button>

          <div className="editor-header-label-edit editor-header-label-disabled">
            <span className="editor-header-input-title">{title}</span>
          </div>

          {reference ? (
            <span className="text-muted-foreground truncate font-mono text-2xl">
              {reference}
            </span>
          ) : null}

          {owner ? (
            <div className="editor-header-indicator editor-tooltip editor-tooltip-center editor-tooltip-multiline">
              <NavbarAvatarImage avatar_url={owner.avatar_url} />
              {ownerTooltip.length > 0 ? (
                <div className="editor-tooltiptext">
                  {ownerTooltip.map((line) => (
                    <span key={line} className="editor-tooltiptext-line">
                      {line}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          {statusInfo ? (
            <div className="editor-header-indicator">
              <span
                className={`editor-item-status item-status-${statusInfo.bucket}`}
                title={statusInfo.label}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default BulkPageHeader;
